import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { KeyRound, Mail } from "lucide-react";
import { AuthShell, OtpBoxes, useResendTimer } from "@/pages/VerifyEmail";

export default function ForgotPassword() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 email, 2 code, 3 nouveau mdp
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useResendTimer(0);

  const sendCode = async () => {
    if (!email) { toast.error(t("fp.enterEmail")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data.message);
      setStep(2); setCooldown(60);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const verifyCode = async () => {
    if (code.length !== 6) { toast.error(t("otp.enter6")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password/verify", { email, code });
      setResetToken(data.reset_token);
      setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || t("otp.invalid")); }
    finally { setBusy(false); }
  };

  const resetPassword = async () => {
    if (pw.length < 8) { toast.error(t("fp.pwdMin8")); return; }
    if (pw !== pw2) { toast.error(t("fp.pwdMismatch")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { reset_token: resetToken, password: pw });
      toast.success(data.message);
      navigate("/login");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <AuthShell title={t("fp.title")}
      subtitle={step === 1 ? t("fp.sub1")
        : step === 2 ? <>{t("fp.sub2a")} <strong>{email}</strong>{t("fp.sub2b")}</>
          : "Choisissez votre nouveau mot de passe."}>
      {step === 1 && (
        <div className="space-y-4">
          <div>
            <Label>Email</Label>
            <Input data-testid="forgot-email" type="email" className="rounded-sm" value={email}
              onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendCode()} />
          </div>
          <Button data-testid="forgot-send" disabled={busy} onClick={sendCode}
            className="w-full h-11 rounded-sm bg-accent hover:bg-accent/90 text-white font-bold gap-2">
            <Mail className="h-4 w-4" /> Recevoir mon code par email
          </Button>
          <p className="text-xs text-muted-foreground text-center">{t("fp.smsLater")}</p>
        </div>
      )}
      {step === 2 && (
        <div className="space-y-5">
          <OtpBoxes value={code} onChange={setCode} testid="forgot-otp" />
          <Button data-testid="forgot-verify" disabled={busy} onClick={verifyCode}
            className="w-full h-11 rounded-sm bg-accent hover:bg-accent/90 text-white font-bold">Valider le code</Button>
          <button data-testid="forgot-resend" onClick={sendCode} disabled={cooldown > 0}
            className="w-full text-sm text-accent font-semibold disabled:text-muted-foreground">
            {cooldown > 0 ? `Renvoyer le code (${cooldown}s)` : "Renvoyer le code"}
          </button>
        </div>
      )}
      {step === 3 && (
        <div className="space-y-4">
          <div>
            <Label>{t("fp.newPwd")}</Label>
            <Input data-testid="reset-pw" type="password" className="rounded-sm" value={pw} onChange={(e) => setPw(e.target.value)} />
            <p className="mt-1 text-xs text-muted-foreground">{t("auth.pwdMin")}</p>
          </div>
          <div>
            <Label>{t("fp.confirmPwd")}</Label>
            <Input data-testid="reset-pw2" type="password" className="rounded-sm" value={pw2} onChange={(e) => setPw2(e.target.value)} />
          </div>
          <Button data-testid="reset-submit" disabled={busy} onClick={resetPassword}
            className="w-full h-11 rounded-sm bg-accent hover:bg-accent/90 text-white font-bold gap-2">
            <KeyRound className="h-4 w-4" /> Modifier mon mot de passe
          </Button>
        </div>
      )}
      <p className="mt-6 text-sm text-muted-foreground text-center">
        <Link data-testid="forgot-back-login" to="/login" className="text-accent font-semibold">{t("fp.backLogin")}</Link>
      </p>
    </AuthShell>
  );
}
